// Knowledge checks (ADR-023; contracts 1.1 section 5): the question set the pack carries for each
// recorded title, and the attempt row a learner's answers make. The Markdown grammar and its parser
// live in @waypoint/content; this is the canonical JSON the build stores and the runtime reads.
import { z } from "zod";
import { Citation } from "./citation.js";
import { PackVersion } from "./pack.js";
import { Id, IsoUtc, NonEmpty, strictObject, Ulid, version } from "./primitives.js";
/** Option letters, in the order a question lists them. */
export const OPTION_IDS = ["a", "b", "c", "d"];
export const OptionId = z.enum(OPTION_IDS);
/** Two or three questions per segment; two to four options per question (HUMAN-GATES row 8). */
export const QUESTIONS_PER_SEGMENT = { min: 2, max: 3 };
export const OPTIONS_PER_QUESTION = { min: 2, max: 4 };
/** A percentage from 0 to 100. */
const Pct = z.number().min(0).max(100);
export const Question = strictObject({
    id: Id, // "<titleId>.<segment>.<ordinal>", assigned by the parser
    prompt: NonEmpty,
    options: z
        .array(strictObject({ id: OptionId, text: NonEmpty }))
        .min(OPTIONS_PER_QUESTION.min)
        .max(OPTIONS_PER_QUESTION.max),
    answer: OptionId,
    why: NonEmpty,
    citation: Citation, // doc and docVersion from pack.yaml; pages from the Why line
    beat: Id.optional(), // a beat of the same title
}).superRefine((q, ctx) => {
    q.options.forEach((o, i) => {
        if (o.id !== OPTION_IDS[i])
            ctx.addIssue({
                code: "custom",
                message: `question ${q.id}: option ${String(i + 1)} is ${o.id}, expected ${String(OPTION_IDS[i])}; options are lettered a, b, c, d in order`,
                path: ["options", i, "id"],
            });
    });
    const matches = q.options.filter((o) => o.id === q.answer).length;
    if (matches !== 1)
        ctx.addIssue({
            code: "custom",
            message: `question ${q.id}: the answer ${q.answer} is exactly one of the options' ids`,
            path: ["answer"],
        });
});
export const KnowledgeCheck = strictObject({
    v: version(1),
    titleId: Id,
    segments: z
        .array(strictObject({
        n: z.number().int().positive(),
        label: NonEmpty,
        questions: z.array(Question).min(QUESTIONS_PER_SEGMENT.min).max(QUESTIONS_PER_SEGMENT.max),
    }))
        .min(1),
}).superRefine((k, ctx) => {
    k.segments.forEach((s, i) => {
        const prev = k.segments[i - 1];
        if (prev !== undefined && s.n <= prev.n)
            ctx.addIssue({
                code: "custom",
                message: `check ${k.titleId}: segment ${String(s.n)} follows segment ${String(prev.n)}; segment numbers rise`,
                path: ["segments", i, "n"],
            });
        s.questions.forEach((q, j) => {
            const expected = `${k.titleId}.${String(s.n)}.${String(j + 1)}`;
            if (q.id !== expected)
                ctx.addIssue({
                    code: "custom",
                    message: `check ${k.titleId}: question id ${q.id} should be ${expected} (<titleId>.<segment>.<ordinal>)`,
                    path: ["segments", i, "questions", j, "id"],
                });
        });
    });
});
/** A learner's attempt: a runtime row, the user's own. Carries ids and the score, never question text. */
export const KnowledgeCheckAttempt = strictObject({
    id: Ulid,
    userId: NonEmpty,
    titleId: Id,
    contentVersion: NonEmpty, // the served contentVersion the attempt was made against (D-76)
    packVersion: PackVersion,
    titleVersionId: Ulid.optional(), // absent when the pack's edition was served
    answers: z.array(strictObject({ questionId: Id, optionId: OptionId })).min(1),
    scorePct: z.number().int().min(0).max(100), // percent correct, rounded half up
    passed: z.boolean(),
    passMarkPct: Pct, // the tenant's mark in force at the time (default 80)
    at: IsoUtc,
}).superRefine((a, ctx) => {
    const seen = new Set();
    a.answers.forEach((x, i) => {
        if (seen.has(x.questionId))
            ctx.addIssue({
                code: "custom",
                message: `attempt ${a.id}: question ${x.questionId} is answered once`,
                path: ["answers", i, "questionId"],
            });
        seen.add(x.questionId);
        if (!x.questionId.startsWith(`${a.titleId}.`))
            ctx.addIssue({
                code: "custom",
                message: `attempt ${a.id}: question ${x.questionId} is not a question of title ${a.titleId}`,
                path: ["answers", i, "questionId"],
            });
    });
    if (a.passed !== a.scorePct >= a.passMarkPct)
        ctx.addIssue({
            code: "custom",
            message: `attempt ${a.id}: passed is true exactly when scorePct is at or above passMarkPct`,
            path: ["passed"],
        });
});
//# sourceMappingURL=knowledge-check.js.map